import React, { useState } from 'react';
// styles are imported globally via styles/main.scss
import Modal from '../components/Modal';
import { EyeIcon } from '../components/Icons';
import GoogleIcon from '../assets/Google-icon.png';
import USIcon from '../assets/us.png';
import PHIcon from '../assets/philippines.png';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { clearAllStarWellbeingCache } from '../utils/cache/starWellbeingCache';

const SLIDE_INTERVAL_MS = 6000;
const SLIDE_COUNT = 4;
const SLIDE_TEXTS = [
  'Emotional, Psychological & Rational Wellness – Balance your mind and emotions',
  'Physical Wellness – Strengthen your body and vitality',
  'Nutritional Wellness – Fuel your body with proper nourishment',
  'Spiritual Wellness – Discover inner peace and purpose'
];

const SLIDE_IMAGES = [
  '/BackgroundSamples/Emotio-Psycho-Rational.png',
  '/BackgroundSamples/Phyisical.png',
  '/BackgroundSamples/Nutritional.png',
  '/BackgroundSamples/Spiritual.png'
];

type LangKey = 'en' | 'fil' | 'bis';

const LANG_OPTIONS: { key: LangKey; label: string; icon: string }[] = [
  { key: 'en', label: 'English', icon: USIcon },
  { key: 'fil', label: 'Filipino', icon: PHIcon },
  { key: 'bis', label: 'Bisaya', icon: PHIcon }
];

interface LoginFormData {
  email: string;
  password: string;
}

interface LoginErrors {
  email?: string;
  password?: string;
  general?: string;
}

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [activeIndex, setActiveIndex] = React.useState<number>(0);
  const [dragDelta, setDragDelta] = React.useState<number>(0);
  const [isDragging, setIsDragging] = React.useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
  const [langOpen, setLangOpen] = React.useState<boolean>(false);
  const [selectedLang, setSelectedLang] = React.useState<LangKey>('en');


  // Login form state
  const [showLoginForm, setShowLoginForm] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState<LoginErrors>({});
  // Forgot Password / OTP Reset state
  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);
  const [showResetPassword, setShowResetPassword] = useState<boolean>(false);
  const [resetEmail, setResetEmail] = useState<string>('');
  const [resetCode, setResetCode] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [resetMessage, setResetMessage] = useState<string>('');

  const slidesRef = React.useRef<HTMLDivElement | null>(null);
  const leftRef = React.useRef<HTMLDivElement | null>(null);
  const langRef = React.useRef<HTMLDivElement | null>(null);
  const startXRef = React.useRef<number>(0);
  const timerRef = React.useRef<number | null>(null);

  const clearAuto = React.useCallback(() => {
    if (timerRef.current !== null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startAuto = React.useCallback(() => {
    clearAuto();
    timerRef.current = window.setInterval(() => {
      setActiveIndex(prev => (prev + 1) % SLIDE_COUNT);
    }, SLIDE_INTERVAL_MS);
  }, [clearAuto]);

  React.useEffect(() => {
    startAuto();
    return clearAuto;
  }, [startAuto, clearAuto]);

  // close language menu when clicking outside
  React.useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    setIsDragging(true);
    clearAuto();
    startXRef.current = e.clientX;
    leftRef.current?.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const delta = e.clientX - startXRef.current;
    setDragDelta(delta);
  };

  const endDrag = (e?: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    setIsDragging(false);
    const containerWidth = leftRef.current?.clientWidth || 1;
    const threshold = Math.max(40, containerWidth * 0.08);
    if (Math.abs(dragDelta) > threshold) {
      if (dragDelta < 0) {
        setActiveIndex(prev => (prev + 1) % SLIDE_COUNT);
      } else {
        setActiveIndex(prev => (prev - 1 + SLIDE_COUNT) % SLIDE_COUNT);
      }
    }
    setDragDelta(0);
    startAuto();
    if (e) leftRef.current?.releasePointerCapture?.(e.pointerId);
  };

  const deltaPercent = React.useMemo(() => {
    const width = leftRef.current?.clientWidth || 1;
    return isDragging ? (dragDelta / width) * 100 : 0;
  }, [dragDelta, isDragging]);

  const selected = React.useMemo(() => LANG_OPTIONS.find(l => l.key === selectedLang) || LANG_OPTIONS[0], [selectedLang]);

  // Handle language change
  const handleLanguageChange = React.useCallback((langKey: LangKey) => {
    setSelectedLang(langKey);
    setLangOpen(false);
  }, []);

  // Login form functions
  const validateLoginForm = (): boolean => {
    const newErrors: LoginErrors = {};

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name as keyof LoginErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateLoginForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Use AuthContext to handle login
        login(data.data.token, data.data.refreshToken, data.data.user);

        // Navigate to panel
        navigate('/panel');
      } else {
        // Handle login error
        setErrors({ general: data.message || 'Login failed. Please try again.' });
      }
    } catch (error) {

      setErrors({ general: 'Login failed. Please check your connection and try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Redirect to Google OAuth via backend
    window.location.href = `${API_CONFIG.BASE_URL}/api/auth/google`;
  };

  // Forgot Password / OTP Handlers
  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail.trim()) {
      setErrors({ general: 'Please enter your email address' });
      return;
    }

    setIsLoading(true);
    setErrors({});
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      });
      const data = await response.json();
      if (response.ok || data.success) {
        setResetMessage('A 6-digit code has been sent to your email.');
        setShowForgotPassword(false);
        setShowResetPassword(true);
      } else {
        setErrors({ general: data.message || 'Failed to send reset email.' });
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setErrors({ general: 'Server error. Please try again later.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetCode.trim() || !newPassword || !confirmPassword) {
      setErrors({ general: 'Please fill out all fields.' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrors({ general: 'Passwords do not match.' });
      return;
    }
    if (newPassword.length < 8) {
      setErrors({ general: 'Password must be at least 8 characters long.' });
      return;
    }

    setIsLoading(true);
    setErrors({});
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetCode, password: newPassword })
      });
      const data = await response.json();
      if (response.ok || data.success) {
        setResetMessage('');
        setShowResetPassword(false);
        setShowLoginForm(true);
        setErrors({ general: 'Password reset successfully! You can now log in.' });
      } else {
        setErrors({ general: data.message || 'Invalid or expired code.' });
      }
    } catch (error) {
      console.error('Reset password error:', error);
      setErrors({ general: 'Server error. Please try again later.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Clear Cache Button - Top Left */}
      <button
        className="clear-cache-btn"
        onClick={() => {
          clearAllStarWellbeingCache();
          alert('All Star of Wellbeing cached data has been cleared!');
        }}
        title="Clear all Star of Wellbeing cached data"
        aria-label="Clear Star of Wellbeing cache"
      >
        🗑️ Clear Cache
      </button>

      <div
        ref={leftRef}
        className={`login-page__left${isDragging ? ' dragging' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
      >
        <div
          ref={slidesRef}
          className={`login-page__slides${isDragging ? ' dragging' : ''}`}
          style={{ transform: `translateX(calc(-${activeIndex * 100}% + ${deltaPercent}%))` }}
        >
          {SLIDE_IMAGES.map((imageUrl, index) => (
            <div key={index} className="login-page__slide">
              <img
                src={imageUrl}
                alt={`Wellness slide ${index + 1}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
            </div>
          ))}
        </div>

        <div className="login-page__overlay">
          <div className="login-page__carousel-indicators">
            {SLIDE_IMAGES.map((_, index) => (
              <button
                key={index}
                className={index === activeIndex ? 'active' : ''}
                onClick={() => {
                  clearAuto();
                  setActiveIndex(index);
                  startAuto();
                }}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
      <div className="login-page__right">
        <div className="lang-dropdown" ref={langRef}>
          <button className="lang-dropdown__button" onClick={() => setLangOpen(prev => !prev)} aria-haspopup="listbox" aria-expanded={langOpen}>
            <img src={selected.icon} alt="" />
            <span>{selected.label}</span>
          </button>
          {langOpen && (
            <ul className="lang-dropdown__menu" role="listbox">
              {LANG_OPTIONS.map(opt => (
                <li key={opt.key}>
                  <button className="lang-dropdown__item" role="option" aria-selected={selectedLang === opt.key} onClick={() => handleLanguageChange(opt.key)}>
                    <img src={opt.icon} alt="" />
                    <span>{opt.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="login-page__content">
          <h1>Hello there!</h1>
          <p>How can we help you today?</p>

          {!showLoginForm && !showForgotPassword && !showResetPassword ? (
            <>
              <button className="btn btn-primary" onClick={() => setShowLoginForm(true)}>Log in</button>
              <Link to="/register" className="btn btn-secondary">Sign up</Link>
              <button className="btn btn-secondary btn-sm" onClick={() => navigate('/register')} style={{ marginTop: '0.5rem', backgroundColor: '#f8f9fa', border: '1px solid #6c757d', color: '#6c757d', display: 'none' }}>Pre Sign up</button>
              <div className="login-page__separator">
                <span>or login using gmail</span>
              </div>
              <button className="login-page__google" onClick={handleGoogleLogin} aria-label="Login with Google">
                <img src={GoogleIcon} alt="Google" />
                <span>Google</span>
              </button>
            </>
          ) : showForgotPassword ? (
            <form onSubmit={handleForgotPasswordSubmit} className="login-form">
              <h3>Reset Password</h3>
              <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
                Enter your email and we'll send you a 6-digit confirmation code.
              </p>
              <div className="form-group">
                <label htmlFor="resetEmail">Email Address</label>
                <input
                  type="email"
                  id="resetEmail"
                  value={resetEmail}
                  onChange={(e) => {
                    setResetEmail(e.target.value);
                    setErrors({});
                  }}
                  className={errors.general ? 'error' : ''}
                  placeholder="Enter your email address"
                />
              </div>

              {errors.general && (
                <div className="error-message general-error">{errors.general}</div>
              )}

              <button type="submit" className="btn btn-primary btn-full" disabled={isLoading}>
                {isLoading ? 'Sending...' : 'Send Verification Code'}
              </button>

              <button
                type="button"
                className="btn btn-secondary btn-full"
                onClick={() => {
                  setShowForgotPassword(false);
                  setShowLoginForm(true);
                  setErrors({});
                }}
              >
                Back to Login
              </button>
            </form>
          ) : showResetPassword ? (
            <form onSubmit={handleResetPasswordSubmit} className="login-form">
              <h3>Enter Verification Code</h3>
              <p style={{ marginBottom: '1rem', color: '#27ae60', fontSize: '0.9rem' }}>
                {resetMessage}
              </p>

              <div className="form-group">
                <label htmlFor="resetCode">6-Digit Code</label>
                <input
                  type="text"
                  id="resetCode"
                  maxLength={6}
                  value={resetCode}
                  onChange={(e) => {
                    setResetCode(e.target.value.replace(/\D/g, ''));
                    setErrors({});
                  }}
                  placeholder="000000"
                  style={{ letterSpacing: '3px', fontWeight: 'bold', textAlign: 'center' }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <div className="password-input">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setErrors({});
                    }}
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <EyeIcon show={showPassword} />
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="password-input">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setErrors({});
                    }}
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <EyeIcon show={showPassword} />
                  </button>
                </div>
              </div>

              {errors.general && (
                <div className="error-message general-error">{errors.general}</div>
              )}

              <button type="submit" className="btn btn-primary btn-full" disabled={isLoading}>
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </button>

              <button
                type="button"
                className="btn btn-secondary btn-full"
                onClick={() => {
                  setShowResetPassword(false);
                  setShowForgotPassword(true);
                  setErrors({});
                }}
              >
                Back
              </button>
            </form>
          ) : (
            <form onSubmit={handleLoginSubmit} className="login-form">
              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? 'error' : ''}
                  placeholder="Enter your email address"
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <div className="password-input">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={errors.password ? 'error' : ''}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <EyeIcon show={showPassword} />
                  </button>
                </div>
                {errors.password && <span className="error-message">{errors.password}</span>}
              </div>

              <div style={{ textAlign: 'right', marginBottom: '15px' }}>
                <a
                  href="#!"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowLoginForm(false);
                    setShowForgotPassword(true);
                    setErrors({});
                  }}
                  style={{ color: '#667eea', fontSize: '0.85rem', textDecoration: 'none' }}
                >
                  Forgot Password?
                </a>
              </div>

              {errors.general && (
                <div className="error-message general-error">
                  {errors.general}
                </div>
              )}

              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>

              <button
                type="button"
                className="btn btn-secondary btn-full"
                onClick={() => setShowLoginForm(false)}
              >
                Back
              </button>
            </form>
          )}

          {/* Star Of Well Being Assessment Button */}
          <button
            className="wellbeing-assessment-btn"
            onClick={() => navigate('/star-of-well-being')}
            aria-label="Start Well Being Assessment"
          >
            Star of Well Being
          </button>
        </div>
      </div>



      <Modal
        isOpen={isModalOpen}
        title="Heads up!"
        onClose={() => setIsModalOpen(false)}
        actionLabel="Got it"
      >
        <div className="modal__badge">
          <span role="img" aria-label="party">🎉</span>
          <strong>This part is still in development</strong>
          <span role="img" aria-label="sparkles">✨</span>
        </div>
        <div className="modal__confetti" aria-hidden="true"></div>
        We'll notify you once it's ready. Meanwhile, feel free to explore!
      </Modal>
    </div>
  );
};

export default LoginPage;


